/**
 * CardLights — la columna de tres llums de la dreta de la card (`plans/calendar-card-signals.md`
 * §6 F1.3-F1.5, D1).
 *
 * Dalt: entrada i sortida · mig: registre · baix: contacte. Quadrats de radi 3 px, 2 px entre ells i
 * centrats en vertical. Colors i símbols de `signal-styles.ts`; el `title` de cada llum és el `reason`
 * del backend.
 *
 * - **Mida:** la calcula la graella (`cardLightSize`, `grid/row-layout.ts`). El símbol, a
 *   `round(mida × 0,66)` px, només es pinta des de `LIGHT_SYMBOL_MIN_SIZE` (12 px).
 * - **Cards estretes:** < 110 px, un sol llum (el més urgent, `LIGHT_URGENCY`) i «+n» a sota si n'hi ha
 *   més d'encesos; < 60 px, cap llum (la franja de pagament es queda).
 * - **Nansa d'estirar:** tapa els últims 8 px de la card; amb nansa, marge dret de 8 px; si no, de 3.
 */

import type { ReactNode } from 'react';
import { Check, Ellipsis, Phone } from 'lucide-react';
import { IconBrandWhatsapp } from '@tabler/icons-react';
import type { BookingSignals } from '@guilleumes/types';
import { cn } from '@/lib/utils';
import {
  LIGHT_STYLES,
  LIGHT_SYMBOL_MIN_SIZE,
  LIGHT_URGENCY,
  stayLightKind,
  taskLightKind,
  type LightKind,
  type LightSymbol,
} from './signal-styles';
import { CALENDAR_PALETTE } from './calendar-theme';

/** Per sota d'aquesta amplada (px) de card, un sol llum i «+n» (§6 F1.4, variant R de la maqueta). */
export const CARD_LIGHTS_SINGLE_BELOW_PX = 110;
/** Per sota d'aquesta amplada (px) de card, cap llum. */
export const CARD_LIGHTS_HIDDEN_BELOW_PX = 60;
/** Mida per defecte si la graella no n'ha posat cap (tests, cards soltes). */
export const DEFAULT_LIGHT_SIZE = 16;

export type LightSlot = 'stay' | 'registration' | 'contact';

export interface CardLight {
  slot: LightSlot;
  kind: LightKind;
  reason: string;
}

/** Els tres llums, de dalt a baix. */
export function cardLights(signals: BookingSignals): CardLight[] {
  return [
    { slot: 'stay', kind: stayLightKind(signals.stay.light), reason: signals.stay.reason },
    {
      slot: 'registration',
      kind: taskLightKind(signals.registration.light),
      reason: signals.registration.reason,
    },
    { slot: 'contact', kind: taskLightKind(signals.contact.light), reason: signals.contact.reason },
  ];
}

/** Els llums encesos (no `OFF`), del més urgent al que menys (a igual urgència, de dalt a baix). */
export function litLightsByUrgency(lights: readonly CardLight[]): CardLight[] {
  const rank = (kind: LightKind) => {
    const i = LIGHT_URGENCY.indexOf(kind);
    return i === -1 ? LIGHT_URGENCY.length : i;
  };
  return lights
    .filter((light) => light.kind !== 'OFF')
    .map((light, index) => ({ light, index }))
    .sort((a, b) => rank(a.light.kind) - rank(b.light.kind) || a.index - b.index)
    .map(({ light }) => light);
}

export interface CardLightsProps {
  signals: BookingSignals;
  /** Mida de cada llum en px (`cardLightSize`). */
  size?: number;
  /** Amplada de la card en px; absent = ampla (els tres llums). */
  cardWidth?: number;
  /** La card té la nansa d'estirar a la dreta: marge de 8 px en lloc de 3. */
  resizeHandle?: boolean;
}

export function CardLights({ signals, size = DEFAULT_LIGHT_SIZE, cardWidth, resizeHandle }: CardLightsProps) {
  if (cardWidth != null && cardWidth < CARD_LIGHTS_HIDDEN_BELOW_PX) return null;

  const lights = cardLights(signals);
  const single = cardWidth != null && cardWidth < CARD_LIGHTS_SINGLE_BELOW_PX;
  let shown: CardLight[] = lights;
  let more: CardLight[] = [];
  if (single) {
    const lit = litLightsByUrgency(lights);
    shown = [lit[0] ?? lights[0]];
    more = lit.slice(1);
  }

  return (
    <div
      className={cn(
        'flex flex-shrink-0 flex-col items-center justify-center gap-[2px] py-[3px]',
        resizeHandle ? 'pr-2' : 'pr-[3px]',
      )}
      data-card-lights={single ? 'single' : 'all'}
    >
      {shown.map((light) => (
        <Light key={light.slot} light={light} size={size} />
      ))}
      {more.length > 0 && (
        <span
          className="text-[9px] font-bold leading-none"
          style={{ color: CALENDAR_PALETTE.cardSecondaryText }}
          title={more.map((light) => light.reason).join(' · ')}
          data-card-lights-more={more.length}
        >
          +{more.length}
        </span>
      )}
    </div>
  );
}

function Light({ light, size }: { light: CardLight; size: number }) {
  const style = LIGHT_STYLES[light.kind];
  const showSymbol = style.symbol !== null && size >= LIGHT_SYMBOL_MIN_SIZE;
  return (
    <span
      className="box-border inline-flex flex-shrink-0 items-center justify-center rounded-[3px]"
      style={{
        width: size,
        height: size,
        background: style.background,
        border: style.borderWidth > 0 ? `${style.borderWidth}px solid ${style.borderColor}` : undefined,
        color: style.color,
      }}
      title={light.reason}
      aria-label={light.reason}
      role="img"
      data-light={light.slot}
      data-light-kind={light.kind}
    >
      {showSymbol && style.symbol && <LightSymbolIcon symbol={style.symbol} px={Math.round(size * 0.66)} />}
    </span>
  );
}

/** El símbol del llum (D13), amb lucide o SVG en línia: els emoji no es veuen igual a 10-12 px. */
function LightSymbolIcon({ symbol, px }: { symbol: LightSymbol; px: number }) {
  const common = { width: px, height: px, 'aria-hidden': true, 'data-light-symbol': symbol } as const;
  let icon: ReactNode;
  switch (symbol) {
    case 'check':
      icon = <Check {...common} strokeWidth={3.5} />;
      break;
    case 'dots':
      icon = <Ellipsis {...common} strokeWidth={3.5} />;
      break;
    case 'phone':
      icon = <Phone {...common} strokeWidth={2.75} />;
      break;
    case 'whatsapp':
      // lucide no porta marques: la de WhatsApp és de Tabler, com al menú lateral.
      icon = <IconBrandWhatsapp {...common} stroke={2.5} />;
      break;
    case 'alert':
      icon = (
        <svg {...common} viewBox="0 0 12 12" fill="currentColor">
          <rect x="4.75" y="1" width="2.5" height="6.5" rx="1.25" />
          <circle cx="6" cy="10" r="1.4" />
        </svg>
      );
      break;
    case 'dot':
      icon = (
        <svg {...common} viewBox="0 0 12 12" fill="currentColor">
          <circle cx="6" cy="6" r="3.5" />
        </svg>
      );
      break;
  }
  return icon;
}

export default CardLights;
